// src/components/pages/Login.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/AuthContext';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  FiMessageSquare, FiCheckCircle, FiCalendar, FiUsers,
  FiClock, FiBarChart2, FiZap, FiShield, FiArrowRight,
  FiStar, FiTrendingUp, FiLayers, FiMail, FiLock, FiEye, FiEyeOff
} from 'react-icons/fi';

const Login = () => {
  const { loginWithGoogle, loginWithEmail, loading } = useAuth();
  const [activeFeature, setActiveFeature] = useState(0);
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async () => {
    try {
      await loginWithGoogle();
    } catch (error) {
      console.error('Error al iniciar sesión:', error);
      toast.error('Error al iniciar sesión con Google');
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error('Ingresa email y contrasena');
      return;
    }
    try {
      await loginWithEmail(email, password);
    } catch {
      // Error handled in AuthContext
    }
  };

  // Auto-rotate features
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature(prev => (prev + 1) % modules.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const modules = [
    {
      icon: <FiMessageSquare size={28} />,
      title: 'Gestor Pro',
      desc: 'Base de conocimiento con respuestas organizadas por categorías. Copia y pega en segundos.',
      color: 'from-blue-500 to-indigo-600',
      bg: 'bg-blue-50',
      text: 'text-blue-600'
    },
    {
      icon: <FiCheckCircle size={28} />,
      title: 'Gestión de Tareas',
      desc: 'Tablero Kanban, listas, calendario y vistas personalizadas. Como Asana, pero tuyo.',
      color: 'from-emerald-500 to-teal-600',
      bg: 'bg-emerald-50',
      text: 'text-emerald-600'
    },
    {
      icon: <FiCalendar size={28} />,
      title: 'Calendario & Cursos',
      desc: 'Programa masterclasses, gestiona instructores y sincroniza con Zoom automáticamente.',
      color: 'from-purple-500 to-violet-600',
      bg: 'bg-purple-50',
      text: 'text-purple-600'
    },
    {
      icon: <FiClock size={28} />,
      title: 'Asistencia & Horas Extra',
      desc: 'Control de asistencia, cálculo automático de horas extra y reportes por empleado.',
      color: 'from-amber-500 to-orange-600',
      bg: 'bg-amber-50',
      text: 'text-amber-600'
    },
    {
      icon: <FiBarChart2 size={28} />,
      title: 'Ventas & Comisiones',
      desc: 'Registro de ventas, cálculo de comisiones por asesor y seguimiento de metas.',
      color: 'from-rose-500 to-pink-600',
      bg: 'bg-rose-50',
      text: 'text-rose-600'
    },
    {
      icon: <FiZap size={28} />,
      title: 'Agente IA',
      desc: 'Asistente inteligente que conoce tu negocio. Genera respuestas y contenido al instante.',
      color: 'from-cyan-500 to-blue-600',
      bg: 'bg-cyan-50',
      text: 'text-cyan-600'
    },
  ];

  const stats = [
    { value: '6', label: 'Módulos integrados' },
    { value: '24/7', label: 'Disponibilidad' },
    { value: 'IA', label: 'Potenciado con' },
    { value: '100%', label: 'En la nube' },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white overflow-x-hidden overflow-y-auto">

      {/* Navbar */}
      <nav className="fixed top-0 w-full z-50 bg-[#0a0a0f]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <FiLayers size={18} />
            </div>
            <span className="text-lg font-bold tracking-tight">Hingenia Suite</span>
          </div>
          <button
            onClick={handleLogin}
            disabled={loading}
            className="px-5 py-2.5 bg-white text-gray-900 rounded-xl text-sm font-semibold hover:bg-gray-100 transition-all active:scale-95 disabled:opacity-50"
          >
            {loading ? 'Cargando...' : 'Iniciar Sesión'}
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6">
        {/* Background effects */}
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-indigo-600/20 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute top-40 left-1/4 w-[300px] h-[300px] bg-purple-600/10 rounded-full blur-[100px] pointer-events-none" />

        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-gray-400 mb-8">
            <FiStar size={14} className="text-amber-400" />
            Plataforma integral para equipos educativos
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-tight mb-6">
            Todo tu equipo.{' '}
            <span className="bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
              Una sola plataforma.
            </span>
          </h1>

          <p className="text-lg sm:text-xl text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed">
            Gestiona respuestas, tareas, cursos, asistencia, ventas y más.
            Potenciado con IA para que tu equipo trabaje más rápido.
          </p>

          <div className="flex flex-col items-center gap-4 mb-16">
            {/* Email login form (collapsible) */}
            {showEmailLogin && (
              <form onSubmit={handleEmailLogin} className="w-full max-w-sm space-y-3 mb-2">
                <div className="relative">
                  <FiMail className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="Email"
                    className="w-full pl-10 pr-4 py-3.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-600 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                  />
                </div>
                <div className="relative">
                  <FiLock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Contrasena"
                    className="w-full pl-10 pr-10 py-3.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-600 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
                    {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                  </button>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl text-sm font-bold shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/40 transition-all active:scale-95 disabled:opacity-50"
                >
                  {loading ? 'Iniciando...' : 'Iniciar Sesion'}
                </button>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
                  <div className="relative flex justify-center text-xs"><span className="px-3 bg-[#0a0a0f] text-gray-600">o</span></div>
                </div>
              </form>
            )}

            <div className="flex flex-col sm:flex-row items-center gap-4">
              <button
                onClick={handleLogin}
                disabled={loading}
                className="group px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl text-base font-bold shadow-2xl shadow-indigo-500/25 hover:shadow-indigo-500/40 transition-all active:scale-95 disabled:opacity-50 flex items-center gap-2"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" opacity=".8"/>
                  <path fill="#fff" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" opacity=".9"/>
                  <path fill="#fff" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" opacity=".7"/>
                  <path fill="#fff" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" opacity=".8"/>
                </svg>
                {loading ? 'Conectando...' : 'Iniciar con Google'}
                <FiArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
              {!showEmailLogin && (
                <button
                  onClick={() => setShowEmailLogin(true)}
                  className="px-8 py-4 border border-white/10 rounded-2xl text-base font-medium text-gray-300 hover:bg-white/5 transition-all flex items-center gap-2"
                >
                  <FiMail size={18} /> Email y contrasena
                </button>
              )}
            </div>

            <div className="flex items-center gap-4 mt-2">
              <Link to="/registro" className="text-sm text-indigo-400 hover:text-indigo-300 font-medium transition-colors">
                Registrar mi empresa
              </Link>
              <span className="text-gray-700">|</span>
              <a href="#modulos" className="text-sm text-gray-400 hover:text-gray-300 transition-colors">
                Ver modulos
              </a>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl mx-auto">
            {stats.map((stat, i) => (
              <div key={i} className="p-4 rounded-2xl bg-white/5 border border-white/5">
                <div className="text-2xl font-bold bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">{stat.value}</div>
                <div className="text-xs text-gray-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modules Section */}
      <section id="modulos" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Una suite completa para tu{' '}
              <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">operación</span>
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              Cada módulo diseñado para resolver un problema real de tu equipo.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {modules.map((mod, i) => (
              <div
                key={i}
                className={`group p-6 rounded-2xl border transition-all duration-500 cursor-default ${
                  activeFeature === i
                    ? 'bg-white/[0.06] border-white/10 shadow-2xl shadow-indigo-500/5 scale-[1.02]'
                    : 'bg-white/[0.02] border-white/5 hover:bg-white/[0.04] hover:border-white/10'
                }`}
                onMouseEnter={() => setActiveFeature(i)}
              >
                <div className={`w-12 h-12 rounded-xl ${mod.bg} ${mod.text} flex items-center justify-center mb-4`}>
                  {mod.icon}
                </div>
                <h3 className="text-lg font-bold mb-2">{mod.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{mod.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Highlights */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8 rounded-2xl bg-gradient-to-b from-white/[0.03] to-transparent border border-white/5">
              <div className="w-14 h-14 rounded-2xl bg-indigo-500/10 flex items-center justify-center mx-auto mb-4">
                <FiZap size={24} className="text-indigo-400" />
              </div>
              <h3 className="font-bold text-lg mb-2">IA Integrada</h3>
              <p className="text-sm text-gray-500">Genera respuestas, mejora contenido y automatiza tareas con inteligencia artificial.</p>
            </div>
            <div className="text-center p-8 rounded-2xl bg-gradient-to-b from-white/[0.03] to-transparent border border-white/5">
              <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                <FiTrendingUp size={24} className="text-emerald-400" />
              </div>
              <h3 className="font-bold text-lg mb-2">Tiempo Real</h3>
              <p className="text-sm text-gray-500">Cambios instantáneos. Tu equipo siempre sincronizado sin refrescar la página.</p>
            </div>
            <div className="text-center p-8 rounded-2xl bg-gradient-to-b from-white/[0.03] to-transparent border border-white/5">
              <div className="w-14 h-14 rounded-2xl bg-purple-500/10 flex items-center justify-center mx-auto mb-4">
                <FiShield size={24} className="text-purple-400" />
              </div>
              <h3 className="font-bold text-lg mb-2">Seguro & Privado</h3>
              <p className="text-sm text-gray-500">Autenticación con Google, datos encriptados y acceso controlado por roles.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div className="p-12 rounded-3xl bg-gradient-to-br from-indigo-600/20 to-purple-600/20 border border-white/10 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/5 to-purple-600/5" />
            <div className="relative z-10">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Comienza a optimizar tu equipo hoy
              </h2>
              <p className="text-gray-400 mb-8 max-w-lg mx-auto">
                Sin tarjeta de credito. Sin instalacion. Registra tu empresa y comienza a trabajar.
              </p>
              <button
                onClick={handleLogin}
                disabled={loading}
                className="px-8 py-4 bg-white text-gray-900 rounded-2xl text-base font-bold hover:bg-gray-100 transition-all active:scale-95 disabled:opacity-50 inline-flex items-center gap-3 shadow-2xl"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {loading ? 'Conectando...' : 'Iniciar con Google'}
                <FiArrowRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-gray-600 text-sm">
            <FiLayers size={16} />
            Hingenia Suite &copy; {new Date().getFullYear()}
          </div>
          <div className="text-gray-600 text-xs">
            Hecho con tecnología de punta para equipos que quieren crecer.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Login;
